/* @flow weak */
import invariant from 'invariant';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { debounce } from 'lodash';

import ChatStore from 'chat/stores/ChatStore';
import focusNode from 'utils/focusNode';
import { makePeopleFilter } from 'chat/lib/ChatSearch';
import errorLogger from 'utils/errorLogger';

import AutosizeInput from 'components/AutosizeInput';
import BoxBody from 'components/BoxBody';
import BoxHeader from 'components/BoxHeader';
import Button from 'components/Button';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog';
import Glyph from 'components/Glyph';
import GroupSelectToInviteDialog from 'groups/components/GroupSelectToInviteDialog';
import KeyConstants from 'constants/KeyConstants';
import ModalTrigger from 'components/ModalTrigger';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import ScrollableArea from 'components/ScrollableArea';
import ToastActions from 'actions/ToastActions';
import Token from 'components/Token';
import { Roles } from 'user/UserConstants';
import { mountIneligibilityAlert } from 'chat/components/PotentialMemberIneligibleDialog';
import ChatListSearchItem from './ChatListSearchItem';

import './styles/AddParticipantModal.scss';

type Props = {
  chat: Chat,
  onAddParticipants?: Function,
  onRequestHide: Function,
};

type State = {
  pickedMembers: PotentialChatMembers,
  max: number,
  members: PotentialChatMembers,
  query: string,
  results: PotentialChatMembers,
  highlight: number,
};

class AddParticipantModal extends React.Component<Props, State> {
  state: State = {
    pickedMembers: new PotentialChatMembers(),
    max: this.props.chat.getMaximumMembers() - this.props.chat.getPresentMemberships().length,
    members: ChatStore.getMembers(),
    query: '',
    results: ChatStore.getMembers(),
    highlight: 0,
  };

  static getStores() {
    return [ChatStore];
  }

  static calculateState(state) {
    const f = makePeopleFilter(state.query);

    const members = ChatStore.getMembers();
    return {
      members,
      results: members.filter(f),
    };
  }

  static prefetch = [
    function() {
      return ChatActions.promisePotentialChatMembers();
    },
  ];

  componentDidMount() {
    this.focusInput();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.members.length === 0 && this.state.members.length > 0) {
      this.performSearch();
    }
  }

  _getInput = () => {
    return ReactDOM.findDOMNode(this.refs.memberSearch);
  };
  _blockResultMouseOverWhileScrolling: boolean = true;

  loadMoreMembers() {
    return ChatActions.promisePotentialChatMembers(this.state.query);
  }

  performSearchOnInputChange = debounce(function() {
    this.performSearch();
    this.setState({ highlight: 0 });
  }, 100);

  performSearch = () => {
    const input = this._getInput();
    const query = input.value;

    const f = makePeopleFilter(query);

    this.setState(
      {
        query,
        results: this.state.members.filter(f),
        highlight: -1,
      },
      this.loadMoreMembers
    );
  };

  focusInput = () => {
    focusNode(this._getInput());
  };

  handleInputChange = () => {
    this.performSearchOnInputChange();
  };

  handleResultMouseOver(index) {
    if (this._blockResultMouseOverWhileScrolling) {
      this._blockResultMouseOverWhileScrolling = false;
      return;
    }
    this.setState({ highlight: index });
  }

  addParticipants = () => {
    const { pickedMembers } = this.state;

    ChatActions.addMembersToChat(this.props.chat, pickedMembers.map(p => p.id))
      .then(() => {
        const message =
          pickedMembers.length === 1
            ? `${pickedMembers.first().getName()} has been added to the ${this.getChatTypeName()}`
            : `${pickedMembers.length} people have been added to the ${this.getChatTypeName()}`;

        ToastActions.showSuccessToast(message);
        this.props.onRequestHide();
        if (this.props.onAddParticipants) {
          this.props.onAddParticipants();
        }
      })
      .catch(errorLogger('ChatActions.addMembersToChat failed'));
  };

  getChatTypeName() {
    return this.props.chat.isActivityType() ? 'activity' : 'conversation';
  }

  clearInput = () => {
    const node = ReactDOM.findDOMNode(this.refs.memberSearch);
    invariant(
      node instanceof window.HTMLElement,
      'AddParticipantModal findDOMNode returned non HTMLElement'
    );
    node.value = '';
    this.refs.memberSearch.handleChange();
    this.handleInputChange();
  };

  handleInputKeyDown = event => {
    const code = event.keyCode;
    switch (code) {
      case KeyConstants.ENTER: {
        const searchItem = this.refs[`searchItem-${this.state.highlight}`];
        if (searchItem) {
          searchItem.click();
        }
        break;
      }
      case KeyConstants.UP:
      case KeyConstants.DOWN: {
        let { highlight } = this.state;
        if (code === KeyConstants.UP) {
          if (highlight >= 0) {
            highlight -= 1;
          } else {
            highlight = this.state.results.length - 1;
          }
        } else {
          if (highlight < this.state.results.length) {
            highlight += 1;
          } else {
            highlight = 0;
          }
        }
        this.setState({ highlight });

        if (highlight >= 0 && highlight < this.state.results.length) {
          const searchItemNode = ReactDOM.findDOMNode(this.refs[`searchItem-${highlight}`]);
          this.refs.scrollArea.scrollIntoView(searchItemNode);
          this._blockResultMouseOverWhileScrolling = true;
        }
        break;
      }
      case KeyConstants.BACKSPACE: {
        const memberSearch = ReactDOM.findDOMNode(this.refs.memberSearch);
        invariant(
          memberSearch instanceof window.HTMLElement,
          'AddParticipantModal 2 findDOMNode returned non HTMLElement'
        );
        if (memberSearch.value.length !== 0) {
          return;
        }

        this.state.pickedMembers.pop();
        this.forceUpdate();
        break;
      }
      default: {
        return;
      }
    }
    event.preventDefault();
  };

  handleOnSelectItem = (member: PotentialChatMember) => {
    if (!member.isEligible()) {
      // $FlowFixMe
      mountIneligibilityAlert(member, this);
      return;
    } else if (this.hasBeenInitialySelected(member)) {
      return;
    }

    if (this.hasBeenPicked(member)) {
      this.unpickMember(member);
    } else {
      this.pickMember(member);
    }
  };

  hasBeenInitialySelected = member => {
    return this.props.chat.isPresentMember(member);
  };

  hasBeenPicked = member => {
    return !!this.state.pickedMembers.get(member.id);
  };

  pickMember = member => {
    this.state.pickedMembers.add(member);
    this.clearInput();
    this.focusInput();
    this.forceUpdate();
  };

  unpickMember = member => {
    this.state.pickedMembers.remove(member);
    this.forceUpdate();
  };

  renderCancel() {
    return (
      <Button
        className="AddParticipantModal-footer-button"
        onClick={this.props.onRequestHide}
        level="secondary"
        eventElementName="AddParticipantModal-footer-button">
        Cancel
      </Button>
    );
  }

  renderConfirmModal(members) {
    const text =
      members.length === 1
        ? `Add 1 person to this ${this.getChatTypeName()}?`
        : `Add ${members.length} people to this ${this.getChatTypeName()}?`;

    return (
      <ConfirmDialog
        eventScreenName="AddParticipantModal"
        title={text}
        actionButtonText="Add"
        cancelButtonText="Cancel"
        onConfirm={this.addParticipants}
        subtext={`New participants can see all the messages in this ${this.getChatTypeName()}.`}
      />
    );
  }

  renderInput() {
    const placeholder = this.state.pickedMembers.length > 0 ? '' : 'Who do you want to add?';

    return (
      <div className="AddParticipantModal-input" onClick={this.focusInput}>
        <span className="AddParticipantModal-input-to">Add:</span>
        <span>{this.renderPickerMemberTokens()}</span>
        <AutosizeInput
          ref="memberSearch"
          className="AddParticipantModal-input-text AddParticipantModal-autoSizeInput"
          placeholder={placeholder}
          onChange={this.handleInputChange}
          onKeyDown={this.handleInputKeyDown}
        />
      </div>
    );
  }

  renderMember = (member, index) => {
    return (
      <ChatListSearchItem
        ref={'searchItem-' + index}
        key={member.id}
        member={member}
        query={this.state.query}
        highlight={member.id === this.state.highlight}
        onSelect={this.handleOnSelectItem}
        selected={this.hasBeenPicked(member)}
        added={this.hasBeenInitialySelected(member)}
        onMouseOver={() => this.handleResultMouseOver(member.id)}
      />
    );
  };

  renderMembers = () => {
    if (this.state.pickedMembers.length > this.state.max) {
      return (
        <div className="AddParticipantModal-limitReached">
          <Glyph
            className="AddParticipantModal-limitGlyph"
            name="chat-close-bubble-fill"
            size="xxxlarge"
          />
          <div className="AddParticipantModal-limitTitle">
            Only {this.props.chat.getMaximumMembers()} people can participate in a conversation.
          </div>
        </div>
      );
    }

    if (this.state.results.length === 0) {
      let button;
      if (CurrentUser.getUser().getRole() === Roles.teacher) {
        const dialog = <GroupSelectToInviteDialog />;
        button = (
          <ModalTrigger modal={dialog}>
            <Button
              className="AddParticipantModal-inviteButton"
              level="secondary"
              color="shamrock"
              size="small"
              eventElementName="AddParticipantModal-inviteButton">
              Add students and parents
            </Button>
          </ModalTrigger>
        );
      }

      return (
        <div className="AddParticipantModal-noResults">
          <div>0 people found</div>
          {button}
        </div>
      );
    }

    return (
      <div className="AddParticipantModal-list">
        <ScrollableArea
          ref="scrollArea"
          className="AddParticipantModal-list-scrollArea"
          showShadows>
          {this.state.results.map(this.renderMember)}
        </ScrollableArea>
      </div>
    );
  };

  renderPickerMemberTokens = () => {
    return this.state.pickedMembers.map(member => {
      return (
        <Token
          onRemove={() => this.unpickMember(member)}
          key={member.id}
          data-test-selector={`add-participant-modal-token-${member.getName()}`}>
          {member.getName()}
        </Token>
      );
    });
  };

  renderSubmit = () => {
    const disabled =
      this.state.pickedMembers.length < 1 || this.state.pickedMembers.length > this.state.max;

    const modal = this.renderConfirmModal(this.state.pickedMembers);

    return (
      <ModalTrigger modal={modal}>
        <Button
          className="AddParticipantModal-footer-button"
          disabled={disabled}
          eventElementName="AddParticipantModal-footer-button"
          data-test-selector="add-participant-modal-submit-button">
          Add
        </Button>
      </ModalTrigger>
    );
  };

  render() {
    return (
      <Dialog {...this.props} size="small" className="AddParticipantModal">
        <BoxHeader title={`Add people to the ${this.getChatTypeName()}`} />
        <BoxBody full>
          <div className="AddParticipantModal-members">
            {this.renderInput()}
            {this.renderMembers()}
          </div>
          <div className="AddParticipantModal-footer">
            {this.renderCancel()}
            {this.renderSubmit()}
          </div>
        </BoxBody>
      </Dialog>
    );
  }
}

export default AddParticipantModal;
